import React, { useContext, useState } from "react";
import {
  Button,
  DatePicker,
  FormField,
  Link,
  SpaceBetween,
  StatusIndicator,
  TextContent,
  TimeInput,
} from "@amzn/awsui-components-react";
import {
  calculatePromoTotal,
  calculateTotalAmount,
  formatCurrency,
  formatDateTime,
  getProductFulfillmentStatus,
} from "../../../helpers/dbdUtils";
import { Section } from "./Section";
import { ExpandableTableSection } from "./ExpandableTableSection";
import { RefundsTable } from "./RefundsTable";
import { ExpandableDbdSection } from "./ExpandableDbdSection";
import { JsonModal } from "../../common/JsonModal";
import {
  createRoyaltyTraceLink,
  createTaxLink,
  generatePromotionLink,
} from "../../../helpers/linkUtils";
import { generateExecutionUrlFromStepFunction } from "../../../helpers/logUtils";
import { ProductDetails } from "../../../types/documents";
import { OrderIdContext, StageContext } from "../../../contexes/OrderIdContext";
import { usePublishMockInvoice } from "../../redrive/hooks/usePublishMockInvoice";
import { Stage } from "../../../lusca-search-api/generated-src";

interface ProductSectionProps {
  product: ProductDetails | undefined;
  productId: string;
  expanded: boolean;
  onChange: (event: { detail: { expanded: boolean } }) => void;
  marketplaceId: string;
  stage: Stage;
  title?: string | React.ReactNode;
}

enum PublishMockInvoiceStatus {
  NOT_STARTED = "NOT_STARTED",
  PENDING = "PENDING",
  LOADING = "LOADING",
  SUCCESSFUL = "SUCCESSFUL",
  FAILED = "FAILED",
}

interface PromotionTableItem {
  promoId: string;
  amount: string;
  taxAmount: string;
}

function renderProductDetails(
  details: ProductDetails["details"],
  productId: string
) {
  return (
    <Section
      title="Product Details"
      keyValuePairs={[
        { label: "Product ID", value: productId },
        { label: "Activity", value: details.activity },
        { label: "GL Product Line", value: details.glProductLineId },
        { label: "Seller of Record", value: details.sellerOfRecordId },
        { label: "Territory", value: details.offerTerritoryCountryCode },
        ...(details.listPrice
          ? [
              {
                label: "List Price",
                value: formatCurrency(
                  details.listPrice.value,
                  details.listPrice.currencyCode
                ),
              },
            ]
          : [
              {
                label: "List Price",
                value: "N/A",
              },
            ]),
        {
          label: "Fulfillment State",
          value: (
            <SpaceBetween size="xs">
              <StatusIndicator type="success">
                {details.fulfillment.state.complete
                  ? `Fulfilled at ${formatDateTime(
                      details.fulfillment.state.complete
                    )}`
                  : "Pending"}
              </StatusIndicator>
              {details.fulfillment.cancellation && (
                <StatusIndicator type="error">
                  Cancelled at{" "}
                  {formatDateTime(details.fulfillment.cancellation.at)}
                </StatusIndicator>
              )}
            </SpaceBetween>
          ),
        },
        ...(details.fulfillment.cancellation
          ? [
              {
                label: "Cancellation Reason",
                value: details.fulfillment.cancellation.reason,
              },
            ]
          : []),
        ...(details.fulfillment.period
          ? [
              {
                label: "Period Start",
                value: formatDateTime(details.fulfillment.period.start),
              },
              {
                label: "Period End",
                value: formatDateTime(details.fulfillment.period.end),
              },
            ]
          : []),
      ]}
    />
  );
}

function renderBillsSection(
  bills: ProductDetails["bills"],
  marketplaceId: string,
  stage: Stage
) {
  return Object.entries(bills).map(([billId, bill]) => {
    const billTotal = calculateTotalAmount(bill.amount);
    const promoTotal = calculatePromoTotal(bill.promotions);
    const finalTotal = billTotal + promoTotal;

    const billDescription = `Original: ${formatCurrency(
      billTotal,
      bill.amount.customerTotalAmountWithoutTax.currencyCode
    )}${
      Object.keys(bill.promotions).length > 0
        ? ` | Promotions: ${formatCurrency(
            promoTotal,
            bill.amount.customerTotalAmountWithoutTax.currencyCode
          )} | Final: ${formatCurrency(
            finalTotal,
            bill.amount.customerTotalAmountWithoutTax.currencyCode
          )}`
        : ""
    }${Object.keys(bill.refunds).length > 0 ? " | Refunded" : ""}`;

    return (
      <Section
        key={billId}
        title="Bill Details"
        description={billDescription}
        keyValuePairs={[
          {
            label: "Bill ID",
            value: billId,
          },
          {
            label: "Tax ID",
            value: bill.taxId ? (
              <Link
                external
                externalIconAriaLabel="Opens in a new tab"
                href={createTaxLink(stage, marketplaceId, bill.taxId)}
              >
                {bill.taxId}
              </Link>
            ) : (
              "Missing"
            ),
          },
          {
            label: "Amount Without Tax",
            value: formatCurrency(
              bill.amount.customerTotalAmountWithoutTax.value,
              bill.amount.customerTotalAmountWithoutTax.currencyCode
            ),
          },
          {
            label: "Tax Amount",
            value: formatCurrency(
              bill.amount.customerTaxAmount?.value,
              bill.amount.customerTaxAmount?.currencyCode
            ),
          },
        ]}
      >
        {Object.keys(bill.promotions).length > 0 && (
          <ExpandableTableSection
            title="Promotions"
            items={Object.entries(bill.promotions).map(
              ([promoId, promotion]): PromotionTableItem => ({
                promoId,
                amount: formatCurrency(
                  promotion.amount.customerTotalAmountWithoutTax.value,
                  promotion.amount.customerTotalAmountWithoutTax.currencyCode
                ),
                taxAmount: formatCurrency(
                  promotion.amount.customerTaxAmount?.value,
                  promotion.amount.customerTaxAmount?.currencyCode
                ),
              })
            )}
            columnDefinitions={[
              {
                header: "Promotion ID",
                cell: (item: PromotionTableItem) => (
                  <Link
                    href={generatePromotionLink(item.promoId, marketplaceId)}
                    external
                  >
                    {item.promoId}
                  </Link>
                ),
              },
              {
                header: "Amount",
                cell: (item: PromotionTableItem) => item.amount,
              },
              {
                header: "Tax Amount",
                cell: (item: PromotionTableItem) => item.taxAmount,
              },
            ]}
          />
        )}

        {Object.keys(bill.refunds).length > 0 && (
          <RefundsTable title="Bill Refunds" refunds={bill.refunds} />
        )}
      </Section>
    );
  });
}

function renderCostsSection(costs: ProductDetails["costs"]) {
  const stage = useContext(StageContext);
  const orderId = useContext(OrderIdContext);

  const [publishMockInvoiceStatus, setPublishMockInvoiceStatus] =
    useState<PublishMockInvoiceStatus>(PublishMockInvoiceStatus.NOT_STARTED);
  const [executionArn, setExecutionArn] = useState<string>("");

  const { publishMockInvoice } = usePublishMockInvoice();

  const handlePublishMockInvoice = async (costPaymentId?: string) => {
    try {
      if (!costPaymentId) return;
      const formattedDate = new Date(invoiceDate).toISOString();
      const response = await publishMockInvoice(
        costPaymentId,
        formattedDate,
        stage
      );
      const { executionArn } = response.data;
      if (executionArn) {
        setExecutionArn(executionArn);
      }
      setPublishMockInvoiceStatus(PublishMockInvoiceStatus.SUCCESSFUL);
    } catch (error) {
      setPublishMockInvoiceStatus(PublishMockInvoiceStatus.FAILED);
    }
  };

  const [invoiceDate, setInvoiceDate] = React.useState<string>(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today.toISOString();
  });
  const [invoiceTime, setInvoiceTime] = React.useState<string>("00:00:00");

  return Object.entries(costs).map(([costId, cost]) => (
    <Section
      key={costId}
      title="Costs"
      keyValuePairs={[
        { label: "Cost Application", value: cost.costApplication },
        { label: "Transaction Type", value: cost.transactionType },
        {
          label: "Amount Without Tax",
          value: cost.amount?.customerTotalAmountWithoutTax
            ? formatCurrency(
                cost.amount.customerTotalAmountWithoutTax.value,
                cost.amount.customerTotalAmountWithoutTax.currencyCode
              )
            : "N/A",
        },
        {
          label: "Tax Amount",
          value: cost.amount?.customerTaxAmount
            ? formatCurrency(
                cost.amount.customerTaxAmount.value,
                cost.amount.customerTaxAmount.currencyCode
              )
            : "N/A",
        },
        {
          label: "Cost Model",
          value:
            cost.costAttributes?.contentPartnerCostAttributes?.costModel ||
            "N/A",
        },
        {
          label: "Cost Features",
          value: cost.costFeatures
            ? Object.values(cost.costFeatures).join(", ")
            : "None",
        },
        {
          label: "CostPaymentId",
          value: cost.costPaymentId ? cost.costPaymentId : "None",
        },
        {
          label: "Cost Payment Status",
          value: cost.costObligationState
            ? `Paid at: ${formatDateTime(cost.costObligationState.complete)}`
            : "Not paid",
        },
        ...(cost.costingPeriod
          ? [
              {
                label: "Costing Period Start",
                value: formatDateTime(cost.costingPeriod.start),
              },
              {
                label: "Costing Period End",
                value: formatDateTime(cost.costingPeriod.end),
              },
            ]
          : []),
      ]}
    >
      {stage &&
        stage !== "Prod" &&
        cost.costPaymentId &&
        ([
          PublishMockInvoiceStatus.SUCCESSFUL,
          PublishMockInvoiceStatus.FAILED,
        ].includes(publishMockInvoiceStatus) ? (
          <StatusIndicator
            type={
              publishMockInvoiceStatus == PublishMockInvoiceStatus.SUCCESSFUL
                ? "success"
                : "error"
            }
          >
            {publishMockInvoiceStatus === PublishMockInvoiceStatus.SUCCESSFUL &&
            executionArn ? (
              <>
                {" View invoice publish step function status: "}
                <Link
                  external
                  externalIconAriaLabel="Opens in a new tab"
                  href={generateExecutionUrlFromStepFunction(
                    stage,
                    executionArn
                  )}
                >
                  here
                </Link>
              </>
            ) : (
              <> Publish MockInvoice Status: {publishMockInvoiceStatus} </>
            )}
          </StatusIndicator>
        ) : (
          <SpaceBetween size="s">
            <Button
              onClick={() => handlePublishMockInvoice(cost.costPaymentId)}
              iconName="refresh"
              disabled={
                publishMockInvoiceStatus === PublishMockInvoiceStatus.LOADING
              }
            >
              {publishMockInvoiceStatus === PublishMockInvoiceStatus.LOADING
                ? "Publishing..."
                : "Publish Mock Invoice"}
            </Button>
            <FormField
              label="Invoice Date"
              constraintText="Today's date is selected by default."
            >
              <DatePicker
                onChange={({ detail }) => {
                  const [hours, minutes, sec] = invoiceTime
                    .split(":")
                    .filter(Boolean);
                  const dateTime = new Date(detail.value);
                  dateTime.setHours(
                    parseInt(hours) || 0,
                    parseInt(minutes) || 0,
                    parseInt(sec) || 0
                  );
                  setInvoiceDate(dateTime.toISOString());
                }}
                value={invoiceDate}
                openCalendarAriaLabel={(selectedDate) => `Select invoice date`}
              />
            </FormField>
            <FormField
              label="Invoice Time (Optional)"
              constraintText="Use hh:mm:ss format."
            >
              <TimeInput
                placeholder="hh:mm:ss"
                value={invoiceTime}
                onChange={({ detail }) => {
                  setInvoiceTime(detail.value);
                  const [hours, minutes, sec] = detail.value
                    .split(":")
                    .filter(Boolean);
                  const dateTime = new Date(invoiceDate);
                  dateTime.setHours(
                    parseInt(hours) || 0,
                    parseInt(minutes) || 0,
                    parseInt(sec) || 0
                  );
                  setInvoiceDate(dateTime.toISOString());
                }}
              />
            </FormField>
          </SpaceBetween>
        ))}
      {cost.refunds && Object.keys(cost.refunds).length > 0 && (
        <RefundsTable refunds={cost.refunds} title="Cost Refunds" />
      )}
      {orderId && (
        <Button
          iconName="external"
          onClick={() =>
            window.open(createRoyaltyTraceLink(orderId, stage), "_blank")
          }
        >
          Open in Royalty Trace
        </Button>
      )}
    </Section>
  ));
}

export function ProductSection({
  product,
  productId,
  expanded,
  onChange,
  marketplaceId,
  stage,
  title,
}: ProductSectionProps) {
  const [jsonModalVisible, setJsonModalVisible] = useState(false);

  if (!product) {
    return (
      <ExpandableDbdSection
        title={title || "Product Details"}
        expanded={expanded}
        onChange={onChange}
        isEmpty
        emptyMessage="No product information available"
      />
    );
  }

  const { details, bills, costs } = product;

  return (
    <ExpandableDbdSection
      title={title || "Product Details"}
      expanded={expanded}
      onChange={onChange}
      headerActions={
        <Button
          iconName="file"
          onClick={(event) => {
            event.stopPropagation();
            setJsonModalVisible(true);
          }}
        >
          View JSON
        </Button>
      }
      description={
        <TextContent>
          <small>
            {getProductFulfillmentStatus(details)}
            {details.listPrice && (
              <>
                {" "}
                | List Price:{" "}
                {formatCurrency(
                  details.listPrice.value,
                  details.listPrice.currencyCode
                )}
              </>
            )}{" "}
            |{" "}
            {Object.keys(costs).length > 0
              ? `Costs: ${Object.values(costs)
                  .map((cost) =>
                    formatCurrency(
                      cost.amount?.customerTotalAmountWithoutTax?.value,
                      cost.amount?.customerTotalAmountWithoutTax?.currencyCode
                    )
                  )
                  .join(", ")}`
              : "No Costs"}
          </small>
        </TextContent>
      }
    >
      <SpaceBetween size="l">
        {renderProductDetails(details, productId)}
        {renderBillsSection(bills, marketplaceId, stage)}
        {Object.keys(costs).length > 0 && renderCostsSection(costs)}
        {jsonModalVisible && (
          <JsonModal
            key={productId}
            title={`Product JSON: ${productId}`}
            content={product}
            visible={jsonModalVisible}
            onDismiss={() => setJsonModalVisible(false)}
          />
        )}
      </SpaceBetween>
    </ExpandableDbdSection>
  );
}
